import React from "react";

/**
 * Returns inline style to align skeleton blocks with table column alignment.
 */
export const getSkeletonAlignmentStyle = (align) => {
  if (align === "r") return { marginLeft: "auto" };
  if (align === "c") return { marginLeft: "auto", marginRight: "auto" };
  return {};
};

/**
 * Strips HTML tags and decodes common entities from a string.
 */
export const stripHtml = (str) => {
  if (typeof str !== "string") return str != null ? String(str) : "";
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/<[^>]*>/g, "")
    .trim();
};

/**
 * Formats a single value for inclusion in a CSV cell.
 * Handles HTML stripping, array values, formula neutralization, and CSV escaping.
 */
export const formatCsvCell = (val) => {
  if (React.isValidElement(val)) return "";
  let text = "";
  if (Array.isArray(val)) {
    text = val
      .filter((item) => item != null && !React.isValidElement(item))
      .map((item) => stripHtml(String(item)))
      .join(", ");
  } else {
    text = stripHtml(val != null ? String(val) : "");
  }
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return text.includes(",") || text.includes('"') || text.includes("\n")
    ? `"${text.replace(/"/g, '""')}"`
    : text;
};

/**
 * Exports table data to a CSV file download.
 *
 * @param {object[]} data - Array of row data objects.
 * @param {Array<{key: string, title?: string, label?: string}>} columns - Column definitions.
 * @param {string|*} title - Table title (string used as filename; other types fall back to "table_export").
 */
export const exportToCsv = (data, columns, title) => {
  if (!Array.isArray(data) || !Array.isArray(columns)) return;

  // Exclude columns whose values are all React elements or all null/undefined (e.g. icon-only columns)
  const exportableColumns = columns.filter(({ key }) =>
    data.some((item) => item[key] != null && !React.isValidElement(item[key])),
  );

  const headerRow = exportableColumns
    .map((c) => {
      const headerText =
        (c.title && String(c.title).trim()) ||
        (c.label && String(c.label).trim()) ||
        c.key ||
        "";
      return formatCsvCell(headerText);
    })
    .join(",");

  const dataRows = data.map((item) =>
    exportableColumns.map(({ key }) => formatCsvCell(item[key])).join(","),
  );

  // Prepend UTF-8 BOM (\uFEFF) for Excel compatibility
  const csvContent = "\uFEFF" + [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  let filename = "table_export";
  if (typeof title === "string") {
    const sanitized = title
      .replace(/[^a-zA-Z0-9\s_-]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (sanitized) filename = sanitized;
  }

  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
